export interface FileDefinitionProps {
  ext: RegExp;
  typeLabel: string;
  /** Index, coverage */
  isTechnical?: boolean;
  /** Genomic, Biologic, etc. */
  isScientific?: boolean;
}

export interface ExtensionInfo extends FileDefinitionProps {
  type: keyof typeof FileDefinition;
}

/**
 * All possible file types.
 * Provides extensions without archive/compression consideration.
 * These regular expressions need to be tested against a full filename, not against the extension only.
 * In case you want to test the extensions only, returned by getFilenameInfo, simply add a "." before.
 */
export const FileDefinition = {
  /* --- Specific case where the content is not known
     as we couldn't extract information from the compressed filename --- */

  ARCHIVE: {
    ext: /\.\b(tar|gz|bgz|gzip|zip|bz2|lzo|xz|tgz|rar|7z|sit)\b(\.|$)/i,
    typeLabel: "Compressed",
  },

  /* --- Generic and Documents --- */

  TEXT: {
    ext: /\.\b(txt|rtf|rtxt|log)\b(\.|$)/i,
    typeLabel: "Text",
  },
  CSV: {
    ext: /\.\b(csv|tsv)\b(\.|$)/i,
    typeLabel: "Comma/TAB-separated Values",
  },
  XML: {
    ext: /\.\b(xml|xhtml|htm|html)\b(\.|$)/i,
    typeLabel: "Markup Language",
  },
  PDF: {
    ext: /\.\b(pdf)\b(\.|$)/i,
    typeLabel: "Portable Document Format",
  },
  WORD: {
    ext: /\.\b(doc|docx)\b(\.|$)/i,
    typeLabel: "Microsoft Word",
  },
  EXCEL: {
    ext: /\.\b(xls|xlsx)\b(\.|$)/i,
    typeLabel: "Microsoft Excel",
  },
  POWERPOINT: {
    ext: /\.\b(ppt|pptx)\b(\.|$)/i,
    typeLabel: "Microsoft PowerPoint",
  },
  OPENDOC: {
    ext: /\.\b(odt|fodt|ods|fods|odp|fodp|odg|fodg|odf)\b(\.|$)/i,
    typeLabel: "OpenDocument",
  },
  IMAGE: {
    ext: /\.\b(jpeg|jpg|tiff|tif|png|apng|gif|bmp|webp|heif|heic|avif)\b(\.|$)/i,
    typeLabel: "Image",
  },
  VIDEO: {
    ext: /\.\b(mpg|mpeg|avi|mov|webm|mp4|mp5|mkv|heiv|av1)\b(\.|$)/i,
    typeLabel: "Video",
  },
  URL: {
    ext: /\.\b(url)\b(\.|$)/i,
    typeLabel: "Link",
  },
  JAVA: {
    ext: /\.\b(jar|jnlp)\b(\.|$)/i,
    typeLabel: "Java",
  },
  PYTHON: {
    ext: /\.\b(py|pyc)\b(\.|$)/i,
    typeLabel: "Python",
  },
  BINARY: {
    ext: /\.\b(bin)\b(\.|$)/i,
    typeLabel: "Binary",
  },
  EXE: {
    ext: /\.\b(exe|bat|cmd|sh|bash)\b(\.|$)/i,
    typeLabel: "Executable",
  },

  /* --- Chemical and Scientific --- */
  // @see http://confluence-gdsel.ch.genedata.com/display/PM/File+Icons

  GFF: {
    ext: /\.\b(gff)\b(\.|$)/i,
    typeLabel: "General Feature Format",
    isScientific: true,
  },
  PDB: {
    ext: /\.\b(pdb)\b(\.|$)/i,
    typeLabel: "Protein Data Bank",
    isScientific: true,
  },
  REL: {
    ext: /\.\b(rel)\b(\.|$)/i,
    typeLabel: "Relative Expression",
    isScientific: true,
  },
  ABS: {
    ext: /\.\b(abs)\b(\.|$)/i,
    typeLabel: "Absolute Expression",
    isScientific: true,
  },
  MSA: {
    ext: /\.\b(msa)\b(\.|$)/i,
    typeLabel: "Multiple Sequence Alignment",
    isScientific: true,
  },
  PROM: {
    ext: /\.\b(prom)\b(\.|$)/i,
    typeLabel: "Promoter",
    isScientific: true,
  },
  FNA: {
    ext: /\.\b(fna)\b(\.|$)/i,
    typeLabel: "Nucleotide FASTA",
    isScientific: true,
  },
  FAA: {
    ext: /\.\b(faa)\b(\.|$)/i,
    typeLabel: "Amino Acid FASTA",
    isScientific: true,
  },
  MREL: {
    ext: /\.\b(mrel)\b(\.|$)/i,
    typeLabel: "Relative Metabolite Data",
    isScientific: true,
  },
  MABS: {
    ext: /\.\b(mabs)\b(\.|$)/i,
    typeLabel: "Absolute Metabolite Data",
    isScientific: true,
  },
  VCF: {
    ext: /\.\b(vcf)\b(\.|$)/i,
    typeLabel: "Variant Call Format",
    isScientific: true,
  },
  TBI: {
    ext: /\.\b(tbi)\b(\.|$)/i,
    typeLabel: "Tabix Index",
    isTechnical: true,
  },
  GDC: {
    ext: /\.\b(gdc)\b(\.|$)/i,
    typeLabel: "Genedata Analyst Annotation",
    isScientific: true,
  },
  GDA: {
    ext: /\.\b(gda)\b(\.|$)/i,
    typeLabel: "Genedata Analyst Data",
    isScientific: true,
  },
  PRK: {
    ext: /\.\b(prk)\b(\.|$)/i,
    typeLabel: "Phylosopher Record Protocol",
    isScientific: true,
  },
  BAM: {
    ext: /\.\b(bam)\b(\.|$)/i,
    typeLabel: "Binary Alignment Map",
    isScientific: true,
  },
  BAI: {
    ext: /\.\b(bai)\b(\.|$)/i,
    typeLabel: "Binary Alignment Map Index",
    isTechnical: true,
  },
  COV: {
    ext: /\.\b(cov)\b(\.|$)/i,
    typeLabel: "Binary Alignment Map Coverage",
    isTechnical: true,
  },
  SAM: {
    ext: /\.\b(sam)\b(\.|$)/i,
    typeLabel: "Sequence Alignment Map",
    isScientific: true,
  },
  GBK: {
    ext: /\.\b(gbk)\b(\.|$)/i,
    typeLabel: "Genbank Format",
    isScientific: true,
  },
  FASTA: {
    ext: /\.\b(fasta|fa)\b(\.|$)/i,
    typeLabel: "FASTA",
    isScientific: true,
  },
  FAI: {
    ext: /\.\b(fai)\b(\.|$)/i,
    typeLabel: "FASTA Index",
    isTechnical: true,
  },
  DICT: {
    ext: /\.\b(dict)\b(\.|$)/i,
    typeLabel: "FASTA Dictionary",
    isTechnical: true,
  },
  FASTQ: {
    ext: /\.\b(fastq|fq)\b(\.|$)/i,
    typeLabel: "FASTQ",
    isScientific: true,
  },
  BED: {
    ext: /\.\b(bed)\b(\.|$)/i,
    isScientific: true,
    typeLabel: "Browser Extensible Data",
  },
  GTF: {
    ext: /\.\b(gtf)\b(\.|$)/i,
    typeLabel: "Reference Genome",
    isScientific: true,
  },
  BT2: {
    ext: /\.\b(bt2)\b(\.|$)/i,
    typeLabel: "Bowtie2 Index",
    isTechnical: true,
  },
} as const satisfies Record<string, FileDefinitionProps>;

/**
 * Get Information and label about all supported extensions
 * @param extension Do not provide a "." before the extensions value
 */
export const getExtensionInfo = (extension: string): ExtensionInfo | null => {
  for (const [key, definition] of Object.entries(FileDefinition)) {
    const ext = `.${extension}`;
    if (ext.match(definition.ext)) {
      return {
        type: key as keyof typeof FileDefinition,
        ...definition,
      };
    }
  }

  return null;
};
